import { Component, input, OnInit, inject } from '@angular/core';
import { AbstractControlOptions, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from '../button/button.component';
import { FeedbackRadioBlockComponent } from './components/feedback-radio-block/feedback-radio-block.component';
import { FeedbackRadioComponent } from './components/feedback-radio/feedback-radio.component';
import { FeedbackIndicatorComponent } from './components/feedback-indicator/feedback-indicator.component';
import { BackendService } from '../../api/bff/backend.service';
import { FeedbackTextAreaComponent } from './components/feedback-text-area/feedback-text-area.component';
import { FeedbackFormData } from './feedback-types';
import { FeedbackCheckBlockComponent } from './components/feedback-check-block/feedback-check-block.component';
import { FeedbackPricingComponent } from './components/feedback-pricing/feedback-pricing.component';
import { FeedbackConsentComponent } from './components/feedback-consent/feedback-consent.component';
import { ModalService } from '../../state/modal/modal.service';
import { FeatureType } from '@shared/interfaces/feedback';
import { firstValueFrom } from 'rxjs';
import { getFeedbackContext } from '../../utils/url/feedback-context';
import { wildcardId } from '../../utils/feedback/feedback';

@Component({
  selector: 'app-feedback-form',
  imports: [
    ButtonComponent,
    ReactiveFormsModule,
    FeedbackRadioBlockComponent,
    FeedbackRadioComponent,
    FeedbackIndicatorComponent,
    FeedbackTextAreaComponent,
    FeedbackCheckBlockComponent,
    FeedbackPricingComponent,
    FeedbackConsentComponent,
  ],
  templateUrl: './feedback-form.component.html',
  styleUrl: './feedback-form.component.css',
})
export class FeedbackFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private backend = inject(BackendService);
  private modal = inject(ModalService);

  public rating = input.required<number>();
  public formData = input.required<FeedbackFormData>();
  public feature = input.required<FeatureType>();
  protected form!: FormGroup;
  protected activePage = 0;

  updatePage(page: number) {
    this.activePage = page;
  }

  ngOnInit() {
    const controllers = this.formData()
      .questions.flat()
      .reduce<{ [key: string]: [null | string | boolean | string[], AbstractControlOptions] }>((acc, q) => {
        const validators = q.validators || [];
        if (q.type === 'radio-block') {
          q.options?.forEach((o) => {
            acc[o.id] = [null, { validators }];
          });
        } else if (q.type === 'check-block' || q.type === 'consent') {
          // we add the control inside the component
        } else if (q.type === 'pricing') {
          q.options?.forEach((o) => {
            acc[o.id] = [null, { validators: [...validators, Validators.pattern('^' + wildcardId + '[0-9]*$')] }];
          });
          acc[q.id] = [wildcardId + 'eur', { validators }];
        } else {
          acc[q.id] = [null, { validators }];
        }
        return acc;
      }, {});
    this.form = this.fb.group(controllers);

    this.modal.onClose(() => {
      this.submit();
    });
  }

  close() {
    // remove the submit on close submit function, since we have submitted already
    this.modal.onClose(undefined);
    this.modal.closeModal();
  }

  submitFeedback() {
    this.submit();
  }

  private submit() {
    const consents: string[] = this.form.value.consent || [];
    const feedbackKeys = Object.keys(this.form.value).filter((key) => key !== 'consent');
    firstValueFrom(
      this.backend.postFeedback({
        feature: this.feature(),
        context: getFeedbackContext(),
        rating: this.rating(),
        consent: { hasConsented: !!consents.length, description: consents.join(', ') || '-' },
        feedback: feedbackKeys.map((key) => ({ questionId: key, answer: this.form.value[key] })),
      }),
    );
  }
}
